import {
    DEFAULT_COMPETITION_LOGO,
    esc,
    eventKey,
    formatDate,
    shortName,
} from "./shared/viewer-utils";

"use strict";
(function () {
    /** @typedef {Bracketr.League & Record<string, any>} PublicEvent */
    /** @typedef {Bracketr.Match & Record<string, any>} PublicMatch */
    /** @typedef {Bracketr.Streamer & Record<string, any>} PublicStreamer */
    /** @typedef {{ date?: string, time?: string, roundName?: string }} PublicGameDay */
    /** @typedef {{ streamer?: PublicStreamer, streamNumber?: number | string, matches?: PublicMatch[] }} PublicLiveStream */
    /**
     * @param {string[]} ids
     * @param {string} sectionSelector
     * @param {string} className
     */
    function getListElement(ids, sectionSelector, className) {
        for (const id of ids) {
            const el = document.getElementById(id);
            if (el)
                return el;
        }
        const section = document.querySelector(sectionSelector) || document.querySelector("main") || document.body;
        const el = document.createElement("div");
        el.id = ids[0];
        el.className = className;
        section.appendChild(el);
        return el;
    }
    const openApplicationsList = getListElement(["openApplicationsList", "applicationsList", "applicationsContainer", "featuredEvents", "featuredEventsList"], "[data-whats-on-section='applications'], .applications-section", "card-carousel");
    const eventsInActionList = getListElement(["eventsInActionList", "activeEventsList", "activeEventsContainer", "eventsContainer"], "[data-whats-on-section='active'], .active-events-section", "card-carousel");
    const liveNowList = getListElement(["liveNowList", "liveStreamsList", "liveStreamsContainer", "liveNowContainer"], "[data-whats-on-section='live'], .live-streams-section", "card-carousel live-carousel");
    /** @param {unknown} dateValue @param {unknown} timeValue @param {unknown} timezoneLabel */
    function formatDateTime(dateValue, timeValue, timezoneLabel) {
        const date = formatDate(dateValue);
        const time = timeValue ? String(timeValue).slice(0, 5) : "Time TBA";
        return `${date} · ${time}${timezoneLabel ? ` ${timezoneLabel}` : ""}`;
    }
    /** @param {PublicEvent} event */
    function renderLogo(event) {
        const src = event.logo || event.gameLogo || DEFAULT_COMPETITION_LOGO;
        return `<img src="${esc(src)}" alt="${esc(event.name || "Bracketr.gg")}">`;
    }
    /** @param {unknown} src @param {unknown} label */
    function renderSmallLogo(src, label) {
        return `<img src="${esc(src || DEFAULT_COMPETITION_LOGO)}" alt="${esc(label || "Bracketr.gg")}">`;
    }
    /** @param {PublicEvent} event */
    function nextGameDay(event) {
        const days = /** @type {PublicGameDay[]} */ (Array.isArray(event.gameDays) ? event.gameDays : []);
        const datedDays = days.filter((day) => day.date).sort((a, b) => {
            const aKey = `${a.date} ${a.time || "00:00"}`;
            const bKey = `${b.date} ${b.time || "00:00"}`;
            return aKey.localeCompare(bKey);
        });
        if (!datedDays.length)
            return null;
        // Use the competition date supplied by the API. Do not use browser UTC
        // conversion here, because Perth late-night events can roll the card forward.
        const todayKey = event.currentDate || new Date().toLocaleDateString("en-CA");
        return datedDays.find((day) => day.date >= todayKey) || datedDays[datedDays.length - 1];
    }
    /** @param {PublicEvent} event */
    function eventMeta(event) {
        return [event.org, event.gameTitle, event.region].filter(Boolean).join(" · ") || "Tournament details";
    }
    /** @param {PublicEvent} event */
    /** @param {PublicEvent} event */
    function seriesPageUrl(event) {
        const leagueId = event.leagueId || event.id || event.eventId || event.seriesId;
        if (leagueId)
            return `/series?leagueId=${encodeURIComponent(leagueId)}`;
        const key = eventKey(event);
        return `/series?event=${encodeURIComponent(key)}`;
    }
    /** @param {PublicEvent} event */
    function seriesLinkAttrs(event) {
        return `href="${esc(seriesPageUrl(event))}" data-series-link="1" data-series-id="${esc(event.leagueId || event.id || event.eventId || event.seriesId || "")}"`;
    }
    /** @param {PublicEvent} event */
    function renderOpenApplicationCard(event) {
        return `
    <article class="event-card application-card">
      <div class="event-top">
        <div class="event-logo">${renderLogo(event)}</div>
        <div>
          <h3>${esc(event.name)}</h3>
          <p class="event-meta">${esc(eventMeta(event))}</p>
        </div>
      </div>
      <p class="event-meta">${esc(event.summary || "Applications are open for this event.")}</p>
      <div class="detail-list compact-details">
        ${event.prizePool ? `<span><b>Prize Pool</b>${esc(event.prizePool)}</span>` : ""}
        ${event.maxTeams ? `<span><b>Teams</b>${esc(event.maxTeams)}</span>` : ""}
        <span><b>Applications Close</b>${esc(formatDate(event.applicationsClose || event.closeApplicationsAt || event.startDate))}</span>
      </div>
      <div class="action-row">
        ${event.applicationUrl ? `<a class="primary-action" href="${esc(event.applicationUrl)}">Join Tournament</a>` : ""}
        <a class="secondary-action" ${seriesLinkAttrs(event)}>Tournament Hub</a>
      </div>
    </article>
  `;
    }
    /** @param {PublicEvent} event */
    function renderInActionCard(event) {
        const nextDay = nextGameDay(event);
        return `
    <article class="event-card action-card">
      <div class="event-top">
        <div class="event-logo">${renderLogo(event)}</div>
        <div>
          <h3>${esc(event.name)}</h3>
          <p class="event-meta">${esc(eventMeta(event))}</p>
        </div>
      </div>
      <p class="event-meta">${esc(event.summary || "This event is currently underway.")}</p>
      <div class="detail-list">
        ${event.prizePool ? `<span><b>Prize Pool</b>${esc(event.prizePool)}</span>` : ""}
        <span><b>Next Game Day</b>${nextDay ? esc(formatDateTime(nextDay.date, nextDay.time, event.timezoneLabel || event.timezone || "")) : "No date set yet"}</span>
        ${event.format ? `<span><b>Format</b>${esc(event.format)}</span>` : ""}
      </div>
      <div class="action-row">
        <a class="secondary-action" ${seriesLinkAttrs(event)}>Open Tournament Hub</a>
      </div>
    </article>
  `;
    }
    /** @param {PublicEvent} event */
    function renderLiveCard(event) {
        const streams = /** @type {PublicLiveStream[]} */ (Array.isArray(event.liveStreams) ? event.liveStreams : []);
        return `
    <article class="live-card live-event-card">
      <div class="live-event-head">
        <div class="event-logo small">${renderLogo(event)}</div>
        <div>
          <h3>${esc(event.name)}</h3>
          <p class="event-meta">${esc(eventMeta(event))}</p>
        </div>
      </div>
      <div class="live-stream-list">
        ${streams.map((stream) => {
            const streamer = stream.streamer || {};
            const firstMatch = (stream.matches || [])[0] || {};
            const streamerName = streamer.name || `Stream ${stream.streamNumber}`;
            const streamerLink = streamer.url
                ? `<a href="${esc(streamer.url)}" target="_blank" rel="noopener">${esc(streamerName)}</a>`
                : `<strong>${esc(streamerName)}</strong>`;
            const rowTag = streamer.url ? "a" : "div";
            const rowAttrs = streamer.url ? ` href="${esc(streamer.url)}" target="_blank" rel="noopener"` : "";
            return `
            <${rowTag} class="live-stream-row live-stream-click-card"${rowAttrs}>
              <div class="stream-logo-pair">
                <div class="mini-logo comp-logo">${renderSmallLogo(event.logo || event.gameLogo, event.name)}</div>
                <div class="mini-logo streamer-logo">${renderSmallLogo(streamer.logo, streamer.name)}</div>
              </div>
              <div>
                <p class="stream-title">Stream ${esc(stream.streamNumber)} : ${esc(streamerName)}</p>
                <p class="match-meta">${esc(firstMatch.roundName || "Game Day")}${firstMatch.time ? ` · ${esc(firstMatch.time)}${event.timezoneLabel ? ` ${esc(event.timezoneLabel)}` : ""}` : ""}</p>
                ${(stream.matches || []).slice(0, 3).map((match) => `
                  <small>${esc(match.team1)} vs ${esc(match.team2)}</small>
                `).join("")}
              </div>
            </${rowTag}>
          `;
        }).join("")}
      </div>
      <div class="action-row"><a class="secondary-action" href="${esc(seriesPageUrl(event))}">Open Tournament Hub</a></div>
    </article>
  `;
    }
    /** @param {PublicEvent} event */
    function isCompletedOrArchivedEvent(event) {
        const stage = String(event.competitionStage || event.competition_stage || "").toLowerCase();
        const status = String(event.status || "").toLowerCase();
        return Number(event.isArchived || event.is_archived || 0) === 1
            || Number(event.isCompleted || event.is_completed || 0) === 1
            || ["archived", "completed", "complete", "finished"].includes(stage)
            || status === "completed";
    }
    /** @param {PublicEvent} event */
    function isEventInAction(event) {
        if (event.applicationsOpen)
            return false;
        if (isCompletedOrArchivedEvent(event))
            return false;
        if (event.generated)
            return true;
        const status = String(event.status || "").toLowerCase();
        return ["active", "running", "in_progress", "scheduled"].includes(status);
    }
    async function loadWhatsOn() {
        try {
            const response = await fetch(`/api/public/whats-on?t=${Date.now()}`, { cache: "no-store" });
            const data = await response.json();
            const allEvents = /** @type {PublicEvent[]} */ (Array.isArray(data.featured) ? data.featured : []);
            const openApplications = /** @type {PublicEvent[]} */ (Array.isArray(data.openApplications)
                ? data.openApplications
                : allEvents.filter((event) => event.applicationsOpen));
            const eventsInAction = /** @type {PublicEvent[]} */ (Array.isArray(data.eventsInAction)
                ? data.eventsInAction
                : allEvents.filter(isEventInAction));
            const liveNow = /** @type {PublicEvent[]} */ (Array.isArray(data.liveNow) ? data.liveNow : []);
            const visibleEventsInAction = eventsInAction.filter((event) => !isCompletedOrArchivedEvent(event));
            const visibleLiveNow = liveNow.filter((event) => !isCompletedOrArchivedEvent(event));
            openApplicationsList.innerHTML = openApplications.length
                ? openApplications.map(renderOpenApplicationCard).join("")
                : `<article class="event-card empty-card">No events are currently open for applications.</article>`;
            eventsInActionList.innerHTML = visibleEventsInAction.length
                ? visibleEventsInAction.map(renderInActionCard).join("")
                : `<article class="event-card empty-card">No events are currently in action.</article>`;
            /** @type {any} */
            const liveNowSection = document.getElementById("liveNowSection");
            if (visibleLiveNow.length) {
                if (liveNowSection)
                    liveNowSection.hidden = false;
                liveNowList.innerHTML = visibleLiveNow.map(renderLiveCard).join("");
            }
            else {
                if (liveNowSection)
                    liveNowSection.hidden = true;
                liveNowList.innerHTML = "";
            }
        }
        catch (error) {
            openApplicationsList.innerHTML = `<article class="event-card empty-card">Could not load open applications.</article>`;
            eventsInActionList.innerHTML = `<article class="event-card empty-card">Could not load events in action.</article>`;
            /** @type {any} */
            const liveNowSection = document.getElementById("liveNowSection");
            if (liveNowSection)
                liveNowSection.hidden = true;
            liveNowList.innerHTML = "";
        }
    }
    loadWhatsOn();
    document.addEventListener("click", (event) => {
        const targetEl = event.target instanceof Element ? event.target : null;
        if (!targetEl)
            return;
        /** @type {any} */
        const button = targetEl.closest("[data-scroll-target]");
        if (!button)
            return;
        const target = document.getElementById(button.dataset.scrollTarget);
        if (!target)
            return;
        const dir = Number(button.dataset.scrollDir || 1);
        const amount = Math.max(320, Math.floor(target.clientWidth * 0.85));
        target.scrollBy({ left: dir * amount, behavior: "smooth" });
    });
    document.addEventListener("click", (event) => {
        const targetEl = event.target instanceof Element ? event.target : null;
        if (!targetEl)
            return;
        /** @type {any} */
        const link = targetEl.closest("a[data-series-link], a[href^='/viewer/bracket'], a[href^='/overlay/bracket']");
        if (!link)
            return;
        const id = link.dataset.seriesId || new URL(link.href, window.location.origin).searchParams.get("leagueId");
        if (!id)
            return;
        event.preventDefault();
        window.location.href = `/series?leagueId=${encodeURIComponent(id)}`;
    });
    function startPublicLiveSocket() {
        if (!("EventSource" in window)) {
            setInterval(loadWhatsOn, 60000);
            return;
        }
        const source = new EventSource("/api/public/events");
        source.addEventListener("match-updated", loadWhatsOn);
        source.addEventListener("league-updated", loadWhatsOn);
        source.addEventListener("applications-updated", loadWhatsOn);
        source.onerror = () => setTimeout(loadWhatsOn, 15000);
    }
    startPublicLiveSocket();
})();
//# sourceMappingURL=whats-on.js.map
